import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom';
import { confirmAlert } from 'react-confirm-alert';
import 'react-confirm-alert/src/react-confirm-alert.css';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';
import { deleteSubjectById, getSubjectsWithPagination } from '@app/services/subject';
import DataTable, { TableStyles } from 'react-data-table-component';
import { Button } from '@app/components';
import CurrencyFormat from 'react-currency-format';
import UpdateSubjectRateModal from './UpdateSubjectRateModal';
import UpdateSubjectPrintThresholdModal from './UpdateSubjectPrintThresholdModal';

const ListSubjects = () => {

    const [subjects, setSubjects] = useState([]);
    const [isLoading, setLoading] = useState(false);
    const canAccess = useSelector((state: any) => state.auth.permissions.subjects);
    const [totalRows, setTotalRows] = useState(0);
    const [perPage, setPerPage] = useState(10);
    const [currentPage, setCurrentPage] = useState(1);
    const [sortField, setSortField] = useState('');
    const [sortDirection, setSortDirection] = useState('');
    const [search, setSearch] = useState({name: '', prefix: '', status: ''});
    const subjectStatus = [{value:'active', label:'Active'}, {value:'inactive', label:'Inactive'}]
    const [show, setShow] = useState(false);
    const handleClose = () => setShow(false);
    const handleShow = () => setShow(true);
    const [payload, setPayload] = useState({});
    const [showThreshold, setShowThreshold] = useState(false);
    const handleThresholdClose = () => setShowThreshold(false);
    const handleThresholdShow = () => setShowThreshold(true);
    const [thresholdPayload, setThresholdPayload] = useState({});

    const customStyle: TableStyles = {
        head: {
            style: {
                color: "#5E5873",
                fontSize: "13px",
                fontWeight: 900,
            },
        },
        headCells: {
            style: {
                borderColor: "#E9ECEF",
                textTransform: "uppercase",
                letterSpacing: "1px",
            },
        },
        headRow: {
            style: {
                backgroundColor: "#F7F7F8",
                minHeight: "44px",
                fontWeight: 500,
            },
        },
    };

    useEffect(() => {
        getAllSubjectsData(1, perPage, sortField, sortDirection);
    },[search]);

    const getAllSubjectsData = async (page, perPage, sortField, sortDirection) => {
        try {
            setLoading(true);
            const subjectsResp = await getSubjectsWithPagination(page, perPage, sortField, sortDirection, search);
            if (subjectsResp.success === true) {
                setLoading(false);
                setSubjects(subjectsResp?.collections?.data);
                setTotalRows(subjectsResp?.collections?.pagination?.total);
            } else {
                setLoading(false);
                toast.error(subjectsResp?.errors[0]?.msg);
            }
        } catch (error: any) {
            setLoading(false);
            toast.error(error?.response?.message || 'Failed');
        }
    };

    const columns = [
        {
            name: 'Subject Name',
            selector: (row: { name: string; }) => row.name,
            sortable: true,
            sortField: 'name',
        },
        {
            name: 'Subject Prefix',
            selector: (row: { prefix: string; }) => row.prefix,
            sortable: true,
            sortField: 'prefix',
        },
        {
            name: 'Rate Per Student',
            selector: (row: { subject_rate: string; }) => row.subject_rate,
            cell: (row: { subject_rate: string; }) => (
                row.subject_rate ? <CurrencyFormat value={ row.subject_rate } decimalScale={2} displayType={'text'} thousandSeparator={true} prefix={'£ '} /> : '---'
            ),
            sortable: true,
            sortField: 'subject_rate',
        },
        {
            name: 'Print Threshold Per Month',
            selector: (row: { worksheet_count_threshold: number; }) => row.worksheet_count_threshold,
            cell: (row: { worksheet_count_threshold: number; }) => {
                if (row.worksheet_count_threshold === null || row.worksheet_count_threshold === undefined) {
                    return '---';
                }
                return `${row.worksheet_count_threshold} worksheets`;
            },
            sortable: true,
            sortField: 'worksheet_count_threshold',
        },
        {
            name: 'Status',
            selector: (row: { status: string; }) => row.status,
            cell: (row: { status: string; }) => (
                row.status === 'active' ? <span className="badge bg-success">Active</span> : <span className="badge bg-danger">Inactive</span>
            ),
            sortable: true,
            sortField: 'status',
        },
        {  
            name: 'Action',
            cell: (row: { id: number, subject_rate: string, name: string, worksheet_count_threshold: number }) => (
              <>
                { canAccess?.edit &&
                    <Link
                    to={`/subjects/edit/${row.id}`}
                    role="button"
                    state={{ id: row.id }}
                    title="Edit Subject"
                    >
                    <i className="fas fa-pen mr-2"/>
                    </Link>
                }
                { canAccess?.['update-rate'] &&
                    <a href='#' onClick={ (e) => { handleUpdateSubjectRate({id:row.id, name:row.name, rate:row.subject_rate})} }>
                        <i className="far fa-money-bill-alt mr-2" title="Update Rate"></i>
                    </a>
                }
                { canAccess?.['update-rate'] &&
                    <a href='#' onClick={ (e) => { handleUpdateThreshold({id:row.id, name:row.name, worksheet_count_threshold:row.worksheet_count_threshold})} }>
                        <i className="fas fa-print mr-2" title="Update Print Threshold"></i>
                    </a>
                }
                { canAccess?.delete &&
                    <a href='#' onClick={ (e) => { deleteSubjectConfirm(e, row.id)} } title="Delete Subject">
                        <i className="fas fa-trash mr-2"/>
                    </a>
                }
              </>
            ),
        },
    ];

    const deleteSubjectConfirm = (e, id) => {
        confirmAlert({
            title: 'Confirm to submit',
            message: 'Are you sure to delete this?',
            buttons: [
                {
                    label: 'Yes',
                    onClick: () => deleteSubject(id)
                },
                {
                    label: 'No',
                    onClick: () => {}
                }
            ]
        });
        e.preventDefault();
    };

    const deleteSubject = async (id) => {
        try {
            setLoading(true);
            const resp = await deleteSubjectById(id);
            if(resp.success === true){
                toast.success(resp?.message);
                setLoading(false);
                getAllSubjectsData(currentPage, perPage, sortField, sortDirection);
            }else{
                toast.error(resp?.errors[0]?.msg);
                setLoading(false);
            }
          } catch (error: any) {
            setLoading(false);
            toast.error(error.response.message || 'Failed');
          }
    };

    const handlePageChange = page => {
        getAllSubjectsData(page, perPage, sortField, sortDirection);
        setCurrentPage(page);
    };

    const handlePerRowsChange = async (newPerPage, page) => {
        getAllSubjectsData(page, newPerPage, sortField, sortDirection);
        setPerPage(newPerPage);
    };

    const handleSort = async (column, sortDirection) => {
        setSortField(column.sortField);
        setSortDirection(sortDirection);
        getAllSubjectsData(currentPage, perPage, column.sortField, sortDirection);
    };

    const handleResetFilter = async () => {
        setSearch({name: '', prefix: '', status: ''});
    };

    const handleUpdateSubjectRate = (subject) => {
        handleShow();
        setPayload(subject);
    }

    const handleUpdateThreshold = (subject) => {
        handleThresholdShow();
        setThresholdPayload(subject);
    }

    const getAllSubjectsAPI =() => {
        getAllSubjectsData(1, perPage, sortField, sortDirection);
    } 

    return (
        <div>
            <div>
                {/* Content Header (Page header) */}
                <section className="content-header">
                    <div className="container-fluid">
                        <div className="row mb-2">
                            <div className="col-sm-6">
                                <h1>Manage Subjects</h1>
                            </div>
                            <div className="col-sm-6">
                                <ol className="breadcrumb float-sm-right">
                                    <li className="breadcrumb-item"><a href="/">Home</a></li>
                                    <li className="breadcrumb-item active">Manage Subjects</li>
                                </ol>
                            </div>
                        </div>
                    </div>{/* /.container-fluid */}
                </section>
                {/* Main content */}
                <section className="content">
                    <div className="container-fluid">
                        <div className="row">
                            <div className="col-12">
                                <div style={{ marginBottom: 10 }} className="row">
                                    
                                </div>
                                <div className="card">
                                    <div className="card-header">
                                        <h3 className="card-title">Manage Subjects</h3>
                                        <div className="col-lg-12 text-right">
                                            { canAccess?.create &&
                                                <Link className="btn btn-primary" to="/subjects/add" > + Add New Subject </Link>
                                            }
                                        </div>
                                    </div>
                                    {/* /.card-header */}
                                    <div className="card-body">
                                        <div className="row mb-2">
                                            <div className="col-sm-3">
                                                <input type="text" name='name' className="form-control" value={search.name} onChange={(e) => setSearch({ ...search, name: e.target.value })} placeholder="Search By Subject Name" />
                                            </div>
                                            <div className="col-sm-3">
                                                <input type="text" name='prefix' className="form-control" value={search.prefix} onChange={(e) => setSearch({ ...search, prefix: e.target.value })} placeholder="Search By Subject Prefix" />
                                            </div>
                                            <div className="col-sm-3">
                                                <select
                                                    className="form-control"
                                                    name="status"
                                                    onChange={(e) => setSearch({ ...search, status: e.target.value })}
                                                    value={search.status}
                                                >
                                                    <option value="">Search By Status</option>
                                                    {subjectStatus.map((obj) => (
                                                        <option key={obj.value} value={obj.value}>
                                                            {obj.label}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                            <div className='col-sm-2'>
                                                <Button type="submit" isLoading={isLoading} onClick={handleResetFilter} className="btn btn-warning">Reset Filter</Button>
                                            </div>
                                        </div>

                                        <DataTable
                                            columns={columns}
                                            data={subjects}
                                            pagination
                                            paginationServer
                                            paginationTotalRows={totalRows}
                                            paginationDefaultPage={currentPage}
                                            onChangePage={handlePageChange}
                                            onChangeRowsPerPage={handlePerRowsChange}
                                            paginationPerPage={perPage}
                                            paginationRowsPerPageOptions={[5, 10, 20, 30, 50, 100]}
                                            progressPending={isLoading}
                                            sortServer
                                            onSort={handleSort}
                                            // paginationComponent
                                            // selectableRows
                                            customStyles={customStyle}
                                        />
                                    </div>
                                    {/* /.card-body */}
                                </div>
                            </div>
                            {/* /.col */}
                        </div>
                        {/* /.row */}
                    </div>
                    {/* /.container-fluid */}
                </section>
                {/* /.content */}
            </div>
            <UpdateSubjectRateModal
                show={show}
                handleClose={handleClose}
                subject={payload}
                getAllSubjectsAPI={getAllSubjectsAPI}
            />
            <UpdateSubjectPrintThresholdModal
                show={showThreshold}
                handleClose={handleThresholdClose}
                subject={thresholdPayload}
                getAllSubjectsAPI={getAllSubjectsAPI}
            />
        </div>
    )
};

export default ListSubjects;
